import { get, set } from 'idb-keyval';

export const OFFLINE_LOGIN_KEY = 'OfflineLoginVerifier';
export const OFFLINE_VERIFIER_ITERATIONS = 600000;
export const ONLINE_LOGIN_REQUIRED = 'An online login is needed.';

export type OfflineLoginRecord = {
  email: string;
  salt: string;
  iterations: number;
  verifier: string;
  token: string;
  profile: any;
};

export type OfflineLoginStore = {
  get: (key: string) => Promise<OfflineLoginRecord | undefined>;
  set: (key: string, value: OfflineLoginRecord) => Promise<void>;
};

const idbStore: OfflineLoginStore = {
  get: key => get<OfflineLoginRecord>(key),
  set: async (key, value) => {
    await set(key, value);
  },
};

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const base64ToBytes = (value: string) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

export const verifierBytesEqual = (left: Uint8Array, right: Uint8Array) => {
  if (left.length !== right.length) return false;
  let diff = 0;
  for (let i = 0; i < left.length; i++) diff |= left[i] ^ right[i];
  return diff === 0;
};

const isBcryptHash = (value: unknown) => typeof value === 'string' && /^\$2[aby]\$/.test(value);

const scrubSecrets = (value: any): any => {
  if (isBcryptHash(value)) return undefined;
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(scrubSecrets);
  const copy: any = {};
  Object.keys(value).forEach(key => {
    if (key === 'password') return;
    const next = scrubSecrets(value[key]);
    if (next !== undefined) copy[key] = next;
  });
  return copy;
};

export const deriveOfflineVerifier = async (password: string, salt: Uint8Array, iterations: number) => {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!subtle) throw new Error('Offline login is not available on this device.');
  const material = await subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = await subtle.deriveBits({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, material, 256);
  return new Uint8Array(bits);
};

export type PendingOfflineLogin = {
  email: string;
  password: string;
  token: string;
  profile: any;
};

// IndexedDB clear() removes every key, including one just written. Save only after that clear.
export const saveOfflineLoginAfterClear = async (
  pending: PendingOfflineLogin | null,
  clearStore: () => Promise<unknown>,
  store: OfflineLoginStore = idbStore
) => {
  await clearStore();
  if (!pending || !pending.token) return;
  try {
    await saveOfflineLogin(pending, store);
  } catch (error) {
    console.error('Could not save the offline login verifier', error);
  }
};

export const saveOfflineLogin = async (
  input: { email: string; password: string; token: string; profile: any },
  store: OfflineLoginStore = idbStore
) => {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iterations = OFFLINE_VERIFIER_ITERATIONS;
  const verifier = await deriveOfflineVerifier(input.password, salt, iterations);
  const record: OfflineLoginRecord = {
    email: input.email.trim().toLowerCase(),
    salt: bytesToBase64(salt),
    iterations,
    verifier: bytesToBase64(verifier),
    token: input.token,
    profile: scrubSecrets(input.profile),
  };
  await store.set(OFFLINE_LOGIN_KEY, record);
  return record;
};

// Keeps the same salt and verifier. A switch has a new server token and no typed password.
export const updateOfflineLoginToken = async (
  input: { email: string; token: string; profile?: any },
  store: OfflineLoginStore = idbStore
) => {
  const record = await store.get(OFFLINE_LOGIN_KEY);
  if (!record || !record.email || !input.token) return undefined;
  if (record.email.trim().toLowerCase() !== (input.email || '').trim().toLowerCase()) return undefined;
  const profile = scrubSecrets(input.profile || record.profile);
  if (profile && typeof profile === 'object') profile.token = input.token;
  const next: OfflineLoginRecord = {
    ...record,
    token: input.token,
    profile,
  };
  await store.set(OFFLINE_LOGIN_KEY, next);
  return next;
};

export const readOfflineLogin = async (email: string, store: OfflineLoginStore = idbStore) => {
  const record = await store.get(OFFLINE_LOGIN_KEY);
  if (!record || !record.email) return undefined;
  if (record.email.trim().toLowerCase() !== email.trim().toLowerCase()) return undefined;
  return record;
};

export const verifyOfflinePassword = async (password: string, record: OfflineLoginRecord) => {
  const derived = await deriveOfflineVerifier(password, base64ToBytes(record.salt), record.iterations);
  return verifierBytesEqual(derived, base64ToBytes(record.verifier));
};
